/** Query param written on brand-hub code links to trace the entry brand. */
export const MARQUE_QUERY_PARAM = 'marque'

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeMarqueHandle(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
}

/**
 * Points the sidebar back link at the entry brand hub when a filter applies.
 * Shopify Liquid cannot read `?marque=`, so this is the storefront path.
 *
 * @param {Element | null | undefined} root
 * @param {string} handle
 * @param {boolean} applied
 * @returns {boolean}
 */
export function syncMarqueBackLink(root, handle, applied) {
  const back = document.querySelector('[data-marque-back]')
  if (!(back instanceof HTMLAnchorElement)) return false

  const fallback =
    back.getAttribute('data-marque-back-href') ||
    back.getAttribute('href') ||
    ''

  if (!back.hasAttribute('data-marque-back-href') && fallback) {
    back.setAttribute('data-marque-back-href', fallback)
  }

  if (!applied || !handle) {
    if (fallback) back.setAttribute('href', fallback)
    return false
  }

  const items =
    root instanceof Element ? root.querySelectorAll('[data-marque-handle]') : []
  for (const item of items) {
    if (
      normalizeMarqueHandle(item.getAttribute('data-marque-handle')) !== handle
    ) {
      continue
    }

    const url = item.getAttribute('data-marque-url')
    if (url) {
      back.setAttribute('href', url)
      return true
    }
  }

  return false
}

/**
 * Shows every brand logo by default (SEO / direct URL). When `?marque=` matches
 * one of the rendered handles, hides the others so the header reflects the hub
 * the shopper came from. Unknown or missing handles keep the full set.
 *
 * @param {Element | null | undefined} root
 * @param {string} [search]
 * @returns {boolean} Whether a matching entry brand was applied
 */
export function applyMarqueEntryFilter(root, search = window.location.search) {
  if (!(root instanceof Element)) return false

  const items = root.querySelectorAll('[data-marque-handle]')
  const handle = normalizeMarqueHandle(
    new URLSearchParams(search).get(MARQUE_QUERY_PARAM)
  )

  if (!handle || items.length === 0) {
    root.removeAttribute('data-marque-filtered')
    for (const item of items) {
      item.hidden = false
    }
    syncMarqueBackLink(root, '', false)
    return false
  }

  let matched = false
  for (const item of items) {
    if (
      normalizeMarqueHandle(item.getAttribute('data-marque-handle')) === handle
    ) {
      matched = true
      break
    }
  }

  if (!matched) {
    root.removeAttribute('data-marque-filtered')
    for (const item of items) {
      item.hidden = false
    }
    syncMarqueBackLink(root, handle, false)
    return false
  }

  root.setAttribute('data-marque-filtered', handle)
  for (const item of items) {
    item.hidden =
      normalizeMarqueHandle(item.getAttribute('data-marque-handle')) !== handle
  }
  syncMarqueBackLink(root, handle, true)
  return true
}

/**
 * @param {ParentNode} [root]
 */
export function initMarqueLogos(root = document) {
  const groups = root.querySelectorAll('[data-marque-logos]')
  for (const group of groups) {
    applyMarqueEntryFilter(group)
  }
}

initMarqueLogos()

document.addEventListener('shopify:section:load', (event) => {
  initMarqueLogos(event.target instanceof Element ? event.target : document)
})

window.addEventListener('pageshow', () => {
  initMarqueLogos()
})
